import { MessageSquare, SmilePlus, Ban } from 'lucide-react'
import {
  SmallPostGlyph,
  LargePostGlyph,
  BadgeGlyph,
  SnippetGlyph,
  FullMessageGlyph,
} from '@/components/outbound/ConfigGlyphs'
import ToggleOptionList from '@/components/ui/ToggleOptionList'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface PostConfigPanelProps {
  agents: Agent[]
  sentByAgentId: number
  onAgentChange: (id: number) => void
  postFormat: 'small' | 'large'
  onFormatChange: (format: 'small' | 'large') => void
  replyType: 'none' | 'reactions' | 'text'
  onReplyTypeChange: (type: 'none' | 'reactions' | 'text') => void
  deliveryStyle: 'badge' | 'snippet' | 'full'
  onDeliveryStyleChange: (style: 'badge' | 'snippet' | 'full') => void
  collectEmail: boolean
  onCollectEmailChange: (checked: boolean) => void
}

export default function PostConfigPanel({
  agents,
  sentByAgentId,
  onAgentChange,
  postFormat,
  onFormatChange,
  replyType,
  onReplyTypeChange,
  deliveryStyle,
  onDeliveryStyleChange,
  collectEmail,
  onCollectEmailChange,
}: PostConfigPanelProps) {
  return (
    <div className="w-[280px] flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto p-4 space-y-5">
      {/* Agent selector */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          De
        </label>
        <select
          value={sentByAgentId}
          onChange={(e) => onAgentChange(parseInt(e.target.value))}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        >
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>
      </div>

      {/* Format */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Formato
        </label>
        <ToggleOptionList
          options={[
            { value: 'small', label: 'Pequeno', icon: <SmallPostGlyph className="w-4 h-4" /> },
            { value: 'large', label: 'Grande', icon: <LargePostGlyph className="w-4 h-4" /> },
          ]}
          value={postFormat}
          onChange={(v) => onFormatChange(v as 'small' | 'large')}
        />
      </div>

      {/* Reply Type */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Tipo de Resposta
        </label>
        <ToggleOptionList
          options={[
            { value: 'text', label: 'Texto', icon: <MessageSquare className="w-4 h-4" /> },
            { value: 'reactions', label: 'Reação', icon: <SmilePlus className="w-4 h-4" /> },
            { value: 'none', label: 'Nenhuma', icon: <Ban className="w-4 h-4" /> },
          ]}
          value={replyType}
          onChange={(v) => onReplyTypeChange(v as 'none' | 'reactions' | 'text')}
        />
      </div>

      {/* Delivery Style */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Enviado como
        </label>
        <ToggleOptionList
          options={[
            { value: 'badge', label: 'Emblema', icon: <BadgeGlyph className="w-4 h-4" /> },
            { value: 'snippet', label: 'Prévia', icon: <SnippetGlyph className="w-4 h-4" /> },
            { value: 'full', label: 'Completa', icon: <FullMessageGlyph className="w-4 h-4" /> },
          ]}
          value={deliveryStyle}
          onChange={(v) => onDeliveryStyleChange(v as 'badge' | 'snippet' | 'full')}
        />
      </div>

      {/* Collect email */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={collectEmail}
            onChange={(e) => onCollectEmailChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            Coletar e-mail
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-500 ml-6">
          Solicitar e-mail de visitantes anônimos
        </p>
      </div>
    </div>
  )
}
